<template>
  <div v-if="mode === 'resetPassword'">
    <div
      id="login"
      class="auth-container text-center bg-light"
    >
      <b-form>
        <img
          class="logo"
          src="../../../assets/images/logo.png"
          alt="uttrly"
        >
        <h1 class="h4 mb-3">
          Reset Password
        </h1>

        <p class="mb-4 text-muted">
          Enter your new password.
        </p>

        <div>
          <div class="password-input">
            <label
              class="sr-only"
              for="password-input"
            >New Password</label>
            <b-input
              id="password-input"
              v-model="newPassword"
              :type="passwordFieldType"
              class="mb-4"
              placeholder="New Password"
              :disabled="resetingPassword || !obbCode"
            />
            <span class="password-visible-toggle">
              <b-icon-eye-fill
                v-if="passwordFieldType === 'password'"
                @click="togglePasswordVisibility"
              />
              <b-icon-eye-slash-fill
                v-else
                @click="togglePasswordVisibility"
              />
            </span>
          </div>

          <b-button
            key="login-button"
            variant="primary"
            type="submit"
            class="btn-lg btn-block"
            :disabled="resetingPassword || !obbCode || !newPassword"
            @click.prevent="resetPassword"
          >
            Reset Password
            <b-spinner v-if="resetingPassword" small label="spinnig" class="ml-1"></b-spinner>
          </b-button>

          <p
            class="copyright mt-4 mb-3 text-muted"
          >
            Dont have an account?
            <b-link
              router-component-name="router-link"
              to="/signup"
            >
              Sign up
            </b-link>
          </p>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue'
import FirebaseService from '@/services/FirebaseService'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { verifyPasswordResetCode, applyActionCode, confirmPasswordReset } from 'firebase/auth'

const email = ref('')
const obbCode = ref('')
const resetingPassword = ref(false)
const mode = ref('')
const newPassword = ref('')
const passwordFieldType = ref('password')
const $route = useRoute()
const $router = useRouter()
const { notify } = useNotification()

onBeforeMount(() => {
  mode.value = $route.query.mode as string || ''
  const actionCode = $route.query.oobCode as string || ''

  // Handle the user management action.
  switch (mode.value) {
    case 'resetPassword':
      handleResetPassword(actionCode)
      break
    case 'verifyEmail':
      handleVerifyEmail(actionCode)
      break
    default:
      // Error: invalid mode.
      notify({ type: 'error', title: 'Error', text: 'An Error occurred, please try again.' })
      $router.push('/login')
      break
  }
})

const handleResetPassword = async (actionCode: string) => {
  const auth = await FirebaseService.auth()
  // Verify the password reset code is valid.
  verifyPasswordResetCode(auth, actionCode).then((verificationEmail) => {
    email.value = verificationEmail
    obbCode.value = actionCode
  }).catch((error) => {
    // Invalid or expired action code.
    notify({ type: 'error', title: 'Error', text: 'An Error occurred, please try again.' })
    console.log(error)
  })
}

const handleVerifyEmail = async (actionCode) => {
  const auth = await FirebaseService.auth()

  // Try to apply the email verification code.
  applyActionCode(auth, actionCode).then(() => {
    // Email address has been verified.
    notify({ type: 'success', title: 'Success', text: 'Email address has been verified, please login.' })
    $router.push('/login')
  }).catch((error) => {
    // Code is invalid or expired. Ask the user to verify their email address
    // again.
    notify({ type: 'error', title: 'Error', text: 'An Error occurred, please try again.' })
    console.log(error)
    $router.push('/login')
  })
}

const resetPassword = async () => {
  const auth = await FirebaseService.auth()
  const oobCode = $route.query.oobCode as string || ''
  resetingPassword.value = true
  confirmPasswordReset(auth, oobCode, newPassword.value).then(() => {
    notify({ type: 'success', title: 'Success', text: 'Password reset successfully, please use your new password to login.' })
    $router.push('/login')
  }).catch((error) => {
    notify({ type: 'error', title: 'Error', text: 'An Error occurred, please try again.' })
    console.log(error)
  })
  resetingPassword.value = false
}

const  togglePasswordVisibility = () => {
  passwordFieldType.value = passwordFieldType.value === 'password' ? 'text' : 'password'
}

</script>

<style lang="scss">
.password-input {
  position: relative;
}

.password-visible-toggle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  cursor: pointer;
}


.auth-container {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: absolute;

  img.logo {
    margin-bottom: 1rem;
  }

  h1 {
    font-weight: 400;
  }

  form {
    width: 100%;
    max-width: 420px;
    padding: 15px;
    margin: auto;

    input#password-input {
      // margin-bottom: 1.2rem;
      border-radius: 0;
    }

    .form-control {
      position: relative;
      box-sizing: border-box;
      height: auto;
      padding: 10px;
      font-size: 1rem;
    }
  }
}
</style>
